import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth';

export type DevProdSyncMode = 'dry-run' | 'apply';
export type DevProdSyncStatus = 'queued' | 'running' | 'completed' | 'failed' | 'blocked' | 'stale';

export interface DevProdSyncProgress {
  phase: string;
  label: string;
  percent: number;
  processed?: number | null;
  total?: number | null;
  namespace?: string | null;
  bucket?: string | null;
  updated_at?: string | null;
}

export interface DevProdSyncReadiness {
  enabled: boolean;
  ready: boolean;
  apply_capable: boolean;
  source_label: string;
  target_label: string;
  missing_env: string[];
  target_errors: string[];
  source_target_same: boolean;
  manifest_dir: string;
  dry_run_ttl_minutes: number;
  lock_stale_minutes: number;
}

export interface DevProdSyncJob {
  job_id: string;
  mode: DevProdSyncMode;
  status: DevProdSyncStatus;
  requested_by?: string | null;
  requested_by_email?: string | null;
  requested_from_origin?: string | null;
  request_ip_hash?: string | null;
  created_at: string;
  requested_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  source_label: string;
  target_label: string;
  settings_fingerprint: string;
  dry_run_job_id?: string | null;
  collections: string[];
  user_policy: 'skip-user-bound' | 'map' | 'import-users';
  include_users: boolean;
  summary: Record<string, number>;
  progress?: DevProdSyncProgress | null;
  dry_run_manifest_digest?: string | null;
  manifest_path?: string | null;
  manifest_digest?: string | null;
  engine_version: string;
  lock_owner?: string | null;
  lock_heartbeat_at?: string | null;
  apply_eligible: boolean;
  error?: string | null;
}

export interface DevProdSyncJobListResponse {
  jobs: DevProdSyncJob[];
}

export interface CreateDevProdSyncJobRequest {
  mode: DevProdSyncMode;
  dry_run_job_id?: string | null;
  collections?: string[] | null;
  user_policy?: 'skip-user-bound' | 'map' | 'import-users';
  include_users?: boolean | null;
}

export interface DevProdSyncManifestResponse {
  job_id: string;
  manifest: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class DevProdSyncService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly baseUrl = `${(environment.devProdSyncApiUrl || environment.apiUrl).replace(/\/$/, '')}/dev-prod-sync`;

  getReadiness(): Observable<DevProdSyncReadiness> {
    return this.http.get<DevProdSyncReadiness>(`${this.baseUrl}/readiness`, this.authOptions());
  }

  createJob(payload: CreateDevProdSyncJobRequest): Observable<DevProdSyncJob> {
    return this.http.post<DevProdSyncJob>(`${this.baseUrl}/jobs`, payload, this.authOptions());
  }

  getJob(jobId: string): Observable<DevProdSyncJob> {
    return this.http.get<DevProdSyncJob>(`${this.baseUrl}/jobs/${encodeURIComponent(jobId)}`, this.authOptions());
  }

  listJobs(limit = 20): Observable<DevProdSyncJobListResponse> {
    return this.http.get<DevProdSyncJobListResponse>(`${this.baseUrl}/jobs`, {
      ...this.authOptions(),
      params: { limit },
    });
  }

  getManifest(jobId: string): Observable<DevProdSyncManifestResponse> {
    return this.http.get<DevProdSyncManifestResponse>(`${this.baseUrl}/jobs/${encodeURIComponent(jobId)}/manifest`, this.authOptions());
  }

  private authOptions(): { headers?: { Authorization: string } } {
    const token = this.auth.getAccessToken();
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  }
}
